import { AbstractControl, UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { DateUtils }                                                 from '@cs/common/utils';
import { isNumeric, isString }                                        from '@cs/core/utils';

export type CompareOperator = 'lessThan' | 'lessThanOrEqual' | 'greaterThan' | 'greaterThanOrEqual' | 'equals' | 'notEquals';


function isEmptyInputValue(value: any): boolean {
	// we don't check for string here so it also works with arrays
	return value == null || value.length === 0;
}


export class CsValidatorsUtils {
	static range(min: number, max: number): ValidatorFn {
		function execute(control: AbstractControl): ValidationErrors | null {
			if (isEmptyInputValue(control.value) || isEmptyInputValue(min) || isEmptyInputValue(max)) {
				return null;  // don't validate empty values to allow optional controls
			}
			const value = parseFloat(control.value);
			// Controls with NaN values after parsing should be treated as not having a
			// minimum, per the HTML forms spec: https://www.w3.org/TR/html5/forms.html#attr-input-min
			return !isNaN(value) && (value < min || value > max)
				   ? {'range': {'range': `${min} <-> ${max}`, 'actual': control.value}}
				   : null;
		}

		return execute;
	}

	/**
		* Validates whether a given control's value falls within a specified date range.
		*
		* The control's value is expected to be a valid date representation, either as an integer
		* in YYYYMMDD format (e.g., 20250101), or as a date-compatible string. If the control's
		* value does not meet these criteria, the function will attempt to parse it as a `Date`.
		*
		* @param min - The minimum timestamp (in seconds) allowed for the date range.
		* @param max - The maximum timestamp (in seconds) allowed for the date range.
		* @return A validation function that checks if the control's value falls
		* within the specified range. Returns a `ValidationErrors` object if validation fails or `null`
		* if the value is valid or if the input is empty.
		*/
	static dateRange(min: number, max: number): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (
				isEmptyInputValue(control.value) ||
				isEmptyInputValue(min) ||
				isEmptyInputValue(max)
			) {
				return null; // don't validate empty values to allow optional controls
			}

			// Check if control.value is an integer in YYYYMMDD format (e.g., 20250101)
			let dateValue: Date;
			if (
				typeof control.value === 'number' ||
				/^\d{8}$/.test(control.value?.toString())
			) {
				// Convert using convertCfDateToJsDate
				dateValue = DateUtils.convertCfDateToJsDate(control.value);
			} else {
				dateValue = new Date(control.value);
			}

			const value = dateValue.getTime() / 1000.0;
			// Controls with NaN values after parsing should be treated as not having a
			// minimum, per the HTML forms spec:
			// https://www.w3.org/TR/html5/forms.html#attr-input-min
			return !isNaN(value) && (value < min || value > max)
										? {
					range: { range: `${min} <-> ${max}`, actual: control.value }
				}
										: null;
		};
	}

	static awbNumber(): ValidatorFn {
		function execute(control: AbstractControl): ValidationErrors | null {
			const awbnumber: unknown    = control.value;
			let awbnumberString: string = null;

			// pass on to build-in validator
			if (awbnumber == null)
				return null;

			// check if number, than convert to string
			if (isNumeric(awbnumber)) {
				awbnumberString = awbnumber.toString();
			} else if (isString(awbnumber)) {
				// check if not empty string
				if (awbnumber.length === 0)
					return null;
				else
					awbnumberString = awbnumber;
			}

			/** Source: Wikipedia */
			const prefix = Number(awbnumberString.slice(0, 3));
			const serial = Number(awbnumberString.slice(3, 3 + 7));
			const check  = Number(awbnumberString.slice(10));

			console.log(`validating ${prefix} ${serial} ${check}`);

			/* calculate remainder */
			const rem = serial % 7;

			/* compare remainder with check digit */
			if (rem !== check) {
				return {
					'awbnumber': {rem, serial, check}
				};
			}
			return null;
		}

		return execute;
	}

	static serverError(): ValidatorFn {
		function execute(control: AbstractControl): ValidationErrors | null {
			return null;
		}

		return execute;
	}

	/**
	 * Validates that the control's value is a finite numeric value.
	 * Empty/null values pass through so optional numeric fields remain valid.
	 */
	static numeric(): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (isEmptyInputValue(control.value)) {
				return null; // let Required handle empty values
			}
			const value = parseFloat(control.value);
			return isNaN(value) || !isFinite(value)
				? { 'number': { actual: control.value } }
				: null;
		};
	}

	/**
	 * Converts a value to a comparable number.
	 * Handles: numbers, date integers (yyyymmdd), ISO date strings (yyyy-mm-dd), Date objects
	 */
	private static toComparableNumber(value: any): number {
		if (typeof value === 'number') {
			return value;
		}
		if (value instanceof Date) {
			return value.getTime();
		}
		if (typeof value === 'string') {
			// Try parsing as number first (handles yyyymmdd integer strings)
			const numValue = parseFloat(value);
			if (!isNaN(numValue) && value.match(/^\d+$/)) {
				return numValue;
			}
			// Try parsing as ISO date (yyyy-mm-dd)
			const dateValue = new Date(value);
			if (!isNaN(dateValue.getTime())) {
				return dateValue.getTime();
			}
			// Fall back to parseFloat for other numeric strings
			return numValue;
		}
		return NaN;
	}

	/**
	 * Validator that compares the current field value with another field's value in the same FormGroup.
	 * @param formGroup The FormGroup containing both fields
	 * @param compareFieldName The name of the field to compare against
	 * @param operator The comparison operator
	 */
	static compareField(formGroup: UntypedFormGroup, compareFieldName: string, operator: CompareOperator): ValidatorFn {
		function execute(control: AbstractControl): ValidationErrors | null {
			const compareControl = formGroup.get(compareFieldName);
			if (!compareControl) {
				console.warn(`[CompareField] Compare control not found: ${compareFieldName}`);
				return null;
			}

			const value = control.value;
			const compareValue = compareControl.value;

			console.log(`[CompareField] Validating: value=${value}, compareValue=${compareValue}, operator=${operator}`);

			// Don't validate if either value is empty
			if (value == null || value === '' || compareValue == null || compareValue === '') {
				console.log(`[CompareField] Skipping - empty value(s)`);
				return null;
			}

			// Convert to comparable numbers (handles dates, integers, etc.)
			const numValue = CsValidatorsUtils.toComparableNumber(value);
			const numCompareValue = CsValidatorsUtils.toComparableNumber(compareValue);

			console.log(`[CompareField] Converted: numValue=${numValue}, numCompareValue=${numCompareValue}`);

			// If either value couldn't be converted, skip validation
			if (isNaN(numValue) || isNaN(numCompareValue)) {
				console.warn(`[CompareField] NaN after conversion`);
				return null;
			}

			let isValid = true;
			switch (operator) {
				case 'lessThan':
					isValid = numValue < numCompareValue;
					break;
				case 'lessThanOrEqual':
					isValid = numValue <= numCompareValue;
					break;
				case 'greaterThan':
					isValid = numValue > numCompareValue;
					break;
				case 'greaterThanOrEqual':
					isValid = numValue >= numCompareValue;
					break;
				case 'equals':
					isValid = numValue === numCompareValue;
					break;
				case 'notEquals':
					isValid = numValue !== numCompareValue;
					break;
			}

			return isValid ? null : {
				'compareField': {
					compareField: compareFieldName,
					operator: operator,
					actual: value,
					compareValue: compareValue
				}
			};
		}

		return execute;
	}
}
